import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@cleanpro/common-ui';
import { Plus, Edit2, Save, X, Trash2, Package } from 'lucide-react';

interface ServiceType {
  id: number;
  name: string;
  description: string;
}

interface Service {
  id: string;
  name: string;
  description: string;
  base_price: number;
  service_type_id: number;
}

interface BusinessService {
  id: string;
  service_id: string;
  is_active: boolean;
  price_override: number | null;
  notes: string | null;
  service: Service;
}

interface BusinessServicesSectionProps {
  businessId: string;
}

export default function BusinessServicesSection({ businessId }: BusinessServicesSectionProps) {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [businessServices, setBusinessServices] = useState<BusinessService[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  
  // Add service form state
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [notes, setNotes] = useState('');

  // Edit service state
  const [editPriceOverride, setEditPriceOverride] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Load service types
      const { data: typesData, error: typesError } = await supabase
        .from('service_types')
        .select('*')
        .order('name');

      if (typesError) throw typesError;
      setServiceTypes(typesData || []);

      // Load all services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*')
        .order('name');

      if (servicesError) throw servicesError;
      setAllServices(servicesData || []);

      // Load business services
      const { data: businessServicesData, error: businessServicesError } = await supabase
        .from('business_services')
        .select(`
          *,
          services:service_id (
            id,
            name,
            description,
            base_price,
            service_type_id
          )
        `)
        .eq('business_id', businessId)
        .eq('is_active', true);

      if (businessServicesError) throw businessServicesError;

      // Transform the data to match our interface
      const transformedData: BusinessService[] = (businessServicesData || []).map((item: Record<string, unknown>) => ({
        id: item.id,
        service_id: item.service_id,
        is_active: item.is_active,
        price_override: item.price_override,
        notes: item.notes,
        service: {
          id: item.services.id,
          name: item.services.name,
          description: item.services.description,
          base_price: Number(item.services.base_price),
          service_type_id: item.services.service_type_id
        }
      }));

      setBusinessServices(transformedData);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadData();
  }, [businessId, loadData]);

  const handleAddService = async () => {
    if (!selectedServiceId) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('business_services')
        .insert({
          business_id: businessId,
          service_id: selectedServiceId,
          is_active: true,
          price_override: priceOverride ? Number(priceOverride) : null,
          notes: notes || null
        });

      if (error) throw error;

      // Reset form and reload data
      setSelectedServiceId('');
      setPriceOverride('');
      setNotes('');
      setIsAdding(false);
      await loadData();
    } catch (error) {
      console.error('Error adding service:', error);
      alert('Failed to add service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateService = async (businessServiceId: string) => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from('business_services')
        .update({
          price_override: editPriceOverride ? Number(editPriceOverride) : null,
          notes: editNotes || null
        })
        .eq('id', businessServiceId);

      if (error) throw error;

      setEditingServiceId(null);
      await loadData();
    } catch (error) {
      console.error('Error updating service:', error);
      alert('Failed to update service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (businessServiceId: string) => {
    if (!confirm('Are you sure you want to remove this service?')) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('business_services')
        .update({ is_active: false })
        .eq('id', businessServiceId);

      if (error) throw error;

      await loadData();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Failed to remove service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (businessService: BusinessService) => {
    setEditingServiceId(businessService.id);
    setEditPriceOverride(businessService.price_override?.toString() || '');
    setEditNotes(businessService.notes || '');
  };

  const cancelEditing = () => {
    setEditingServiceId(null);
    setEditPriceOverride('');
    setEditNotes('');
  };

  const getServicesByType = (serviceTypeId: number) => {
    return businessServices.filter(bs => bs.service.service_type_id === serviceTypeId);
  };

  const getAvailableServices = () => {
    const assignedServiceIds = businessServices.map(bs => bs.service_id);
    return allServices.filter(s => !assignedServiceIds.includes(s.id));
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex justify-center items-center py-8">
          <p className="text-gray-500">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <Package className="h-5 w-5 mr-2 text-gray-400" />
          Services Offered
        </h2>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Service
          </button>
        )}
      </div>

      {/* Add Service Form */}
      {isAdding && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Add New Service</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor="service-select" className="block text-sm font-medium text-gray-700 mb-1">
                Service
              </label>
              <select
                id="service-select"
                value={selectedServiceId}
                onChange={(e) => setSelectedServiceId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a service...</option>
                {serviceTypes.map(type => {
                  const availableServices = getAvailableServices().filter(s => s.service_type_id === type.id);
                  if (availableServices.length === 0) return null;
                  
                  return (
                    <optgroup key={type.id} label={type.name}>
                      {availableServices.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name} (${service.base_price})
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <div>
              <label htmlFor="price-override" className="block text-sm font-medium text-gray-700 mb-1">
                Price Override (Optional)
              </label>
              <input
                id="price-override"
                type="number"
                step="0.01"
                min="0"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                placeholder="Leave empty to use base price"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any special notes about this service..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddService}
                disabled={!selectedServiceId || saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition"
              >
                {saving ? 'Adding...' : 'Add Service'}
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setSelectedServiceId('');
                  setPriceOverride('');
                  setNotes('');
                }}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services List */}
      {businessServices.length === 0 ? (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No services configured yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {serviceTypes.map(type => {
            const typeServices = getServicesByType(type.id);
            if (typeServices.length === 0) return null;

            return (
              <div key={type.id}>
                <h3 className="text-md font-semibold text-gray-700 mb-3">{type.name}</h3>
                <div className="space-y-2">
                  {typeServices.map(bs => (
                    <div key={bs.id} className="p-4 border border-gray-200 rounded-lg">
                      {editingServiceId === bs.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-gray-900">{bs.service.name}</p>
                            <p className="text-sm text-gray-500">Base Price: ${bs.service.base_price}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Override
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editPriceOverride}
                              onChange={(e) => setEditPriceOverride(e.target.value)}
                              placeholder="Leave empty to use base price"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Notes
                            </label>
                            <textarea
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdateService(bs.id)}
                              disabled={saving}
                              className="flex items-center px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </button>
                            <button
                              onClick={cancelEditing}
                              disabled={saving}
                              className="flex items-center px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{bs.service.name}</p>
                            <p className="text-sm text-gray-600 mt-1">{bs.service.description}</p>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm text-gray-500">
                                Base Price: ${bs.service.base_price}
                              </p>
                              {bs.price_override && (
                                <p className="text-sm font-medium text-indigo-600">
                                  Override Price: ${bs.price_override}
                                </p>
                              )}
                              {bs.notes && (
                                <p className="text-sm text-gray-600 italic">
                                  Note: {bs.notes}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => startEditing(bs)}
                              className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteService(bs.id)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                              title="Remove"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
